import { z } from "zod";

import { dateSchema } from "../dates";
import { LdoPaginated, paginatedSchema } from "../pagination";

const bulkExportTypeSchema = z.union([
  z.literal("composite_map"),
  z.literal("bulk_file"),
  z.literal("ror"),
]);

export type BulkExportType = z.infer<typeof bulkExportTypeSchema>;

const exportStatusSchema = z.union([
  z.literal("incomplete"),
  z.literal("submitted"),
  z.literal("error"),
  z.literal("complete"),
  z.literal("shared"),
  z.literal("archived"),
]);
export type BulkExportStatus = z.infer<typeof exportStatusSchema>;
export const EXPORT_STATUSES_EXCEPT_ARCHIVED = [
  "incomplete",
  "submitted",
  "error",
  "complete",
  "shared",
];

const bulkExportFilesSchema = z.record(
  z.string(),
  z.object({
    created_at: dateSchema,
    size: z.number(),
    etag: z.string(),
  })
);

const scenarioIdSchema = z.array(z.number());

const bulkFileConfigSchema = z.object({
  excel_file_name: z.string(),
});
export type BulkFileConfig = z.infer<typeof bulkFileConfigSchema>;

export const COMPOSITE_MAPS = [
  "max_velocity",
  "max_waterdepth",
  "max_waterlevel",
  "arrival_times",
  "rate_of_rise",
  "rise_period",
  "inundation_sources",
];
const compositeMaps = z.union([
  z.literal("max_velocity"),
  z.literal("max_waterdepth"),
  z.literal("max_waterlevel"),
  z.literal("arrival_times"),
  z.literal("rate_of_rise"),
  z.literal("rise_period"),
  z.literal("inundation_sources"),
]);

const compositeMapSchema = z.object({
  cell_size: z.number(),
  raster_types: z.array(compositeMaps),
});
export type CompositeMapConfig = z.infer<typeof compositeMapSchema>;

export const ROR_CALCULATION_VARIANTS = ["10", "100", "1000,onb", "1000,bes", "10000"];
const rorCalculationVariants = z.union([
  z.literal("10"),
  z.literal("100"),
  z.literal("1000,onb"),
  z.literal("1000,bes"),
  z.literal("10000"),
]);
export type RorCalculationVariant = z.infer<typeof rorCalculationVariants>;

export const ROR_DANGER_MAPS = ["vg1", "vg2", "vg3", "gg1", "gg2", "gg3", "gg4", "gg5"];
const rorDangerMaps = z.union([
  z.literal("vg1"),
  z.literal("vg2"),
  z.literal("vg3"),
  z.literal("gg1"),
  z.literal("gg2"),
  z.literal("gg3"),
  z.literal("gg4"),
  z.literal("gg5"),
]);
export type RorDangerMap = z.infer<typeof rorDangerMaps>;

export const ROR_RISK_MAPS = ["vr1", "vr2", "vr3", "vr4", "gr1", "gr2", "gr3"];
const rorRiskMaps = z.union([
  z.literal("vr1"),
  z.literal("vr2"),
  z.literal("vr3"),
  z.literal("vr4"),
  z.literal("gr1"),
  z.literal("gr2"),
  z.literal("gr3"),
]);
export type RorRiskMap = z.infer<typeof rorRiskMaps>;

const rorSchema = z.object({
  cell_size: z.number(),
  calculation_variant: rorCalculationVariants,
  danger_maps: z.array(rorDangerMaps),
  risk_maps: z.array(rorRiskMaps),
});
export type RorConfig = z.infer<typeof rorSchema>;

export const bulkExportSchema = z.object({
  name: z.string(),
  description: z.string(),
  id: z.number(),
  type: bulkExportTypeSchema,
  created_at: dateSchema,
  created_by: z.object({
    id: z.string(),
    name: z.string(),
  }),
  updated_at: dateSchema,
  status: exportStatusSchema,
  scenario_ids: scenarioIdSchema,
  files: bulkExportFilesSchema,
  config: bulkFileConfigSchema.or(compositeMapSchema).or(rorSchema),
  errors: z.array(z.object({ msg: z.string() })),
  rasters: z.array(
    z.object({
      type: z.string(),
      source_id: z.string(),
      id: z.string(),
      wms_layer: z.string(),
    })
  ),
  wms_layers: z.array(z.object({ name: z.string(), endpoint: z.string(), layer: z.string() })),
});

export const bulkExportsListSchema = paginatedSchema(bulkExportSchema);

export type ApiBulkExport = z.infer<typeof bulkExportSchema>;
export type PaginatedApiBulkExportList = LdoPaginated<ApiBulkExport>;

export const bulkExportCanStartSchema = z.object({
  can_start: z.boolean(),
});

export type ApiBulkExportCanStart = z.infer<typeof bulkExportCanStartSchema>;
